import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { TriggerProps as PopupTriggerProps } from '@atlaskit/popup/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { TriggerProps as TooltipTriggerProps } from '@atlaskit/tooltip/types';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { usePageTitle } from '@confluence/content-title/entry-points/usePageTitle.ts';
import { type DevDocsBylineConnectionAncestor as DevDocsBylineConnectionAncestorType } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { getDevDocsProviderIcon } from '@confluence/dev-docs/entry-points/DevDocsProviderIcon.ts';
import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext.ts';

import {
	ANALYTICS_SOURCE,
	BYLINE_ACTION_SUBJECT_ID,
	MENU_CLOSE_ACTION_SUBJECT_ID,
	MENU_SOURCE,
} from './analytics.ts';
import { BylineConnectionsTrigger, BylineConnectionsWrapper } from './BylineConnectionsTrigger.tsx';
import { BylineConnectionsPlaceholder } from './ssr/BylineConnectionsPlaceholder.tsx';
import { getReservedBylineIconCount } from './ssr/outboundBylineSsrState.ts';
import { useOutboundBylineSsrState } from './ssr/useOutboundBylineSsrState.ts';
import { ConnectionsByLineMenu } from './ConnectionsByLineMenu.tsx';
import { useBylineConnectionsData } from './hooks/useBylineConnectionsData.ts';
import { useBylineConnectionsLoadTiming } from './hooks/useBylineConnectionsLoadTiming.ts';
import { TwgTraceIdAnalyticsWrapper } from './TwgTraceIdAnalyticsWrapper.tsx';
import { ShareContextModalLoadable } from './teamwork-graph-preview-panel/share-context-modal/ShareContextModalLoadable.tsx';
import { useTeamworkGraphPreviewPanel } from './teamwork-graph-preview-panel/useTeamworkGraphPreviewPanel.tsx';

const i18n = defineMessages({
	tooltipTwg: {
		id: 'byline-connections.tooltip-twg-v2',
		defaultMessage: "See how this content connects to your team's work",
		description:
			"Tooltip shown on hover over the connections byline item, describing that it shows how the current page connects to the team's work.",
	},
});

/**
 * Forwards a node to a React ref that may be either a callback ref or an object
 * ref. Exported for unit testing so both ref shapes are covered.
 */
export const assignRef = (
	ref: React.Ref<HTMLButtonElement>,
	node: HTMLButtonElement | null,
): void => {
	if (typeof ref === 'function') {
		ref(node);
	} else if (ref && 'current' in ref) {
		(ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
	}
};

/**
 * Resolves the byline tooltip text. Returns an empty string while the menu is
 * open (the tooltip is suppressed then), otherwise the Teamwork Graph label.
 */
const getTooltipContent = (isOpen: boolean, formatMessage: IntlShape['formatMessage']): string => {
	if (isOpen) {
		return '';
	}
	return formatMessage(i18n.tooltipTwg);
};

type BylineConnectionsProps = {
	contentId: string;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
};

export type DevDocsBylineConnectionAncestor = DevDocsBylineConnectionAncestorType;

const BylineConnectionsInner: FC<BylineConnectionsProps> = ({
	contentId,
	connectionAncestors,
	pageVersion,
	pageUpdatedAt,
}) => {
	const { title, error: pageTitleError } = usePageTitle({ contentId });
	const { formatMessage } = useIntl();
	const { Popup } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);
	// The share-context modal is owned here, not by the menu's button: the popup closes on any
	// outside click, and a click inside the modal's portal is an outside click, so a modal owned
	// inside the popup would be unmounted along with it.
	const [isShareContextOpen, setIsShareContextOpen] = useState(false);
	const [hasOpenedShareContext, setHasOpenedShareContext] = useState(false);
	// The popup's focus trap returns focus to the trigger on close by calling
	// `.focus()` (without `preventScroll`), which scrolls the trigger back into
	// view. For a link navigation that fights the in-page scroll, so we disable
	// the trap's focus return (`shouldReturnFocus={false}`) and instead restore
	// focus ourselves on non-navigation closes (Esc / outside click) using
	// `preventScroll` — keeping keyboard a11y without the scroll jump.
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const navigatedRef = useRef(false);
	const hasFiredViewedRef = useRef(false);
	const { outgoing, incoming, relatedContent, devDocsConnection, atAGlance } =
		useBylineConnectionsData({
			contentId,
			connectionAncestors,
			pageVersion,
			pageUpdatedAt,
			pageTitle: pageTitleError ? null : title,
		});
	const { groups, loading, links, refreshLinkUrl } = outgoing;
	const { links: incomingLinks, loading: incomingLinksLoading } = incoming;
	const { links: relatedContentLinks } = relatedContent;
	const { openTeamworkGraphPreviewPanel, openTeamworkGraphPreviewPanelWithRovo } =
		useTeamworkGraphPreviewPanel(contentId);
	const devDocsIcons = useMemo(() => {
		const devDocsProviderIcon = getDevDocsProviderIcon(devDocsConnection);
		return devDocsProviderIcon ? [devDocsProviderIcon] : undefined;
	}, [devDocsConnection]);

	// The byline renders whenever there is any connection signal. Outbound smart links and Dev Docs
	// keep their existing behaviour; inbound-only pages (incoming links present, no outbound, no Dev
	// Docs) are additive. Inbound is only awaited when there is no outbound/Dev Docs signal, so the
	// outbound placeholder → trigger handoff timing is unchanged. CCINT-8932
	const hasOutboundConnection = links.length > 0 || devDocsConnection !== null;
	// Related content is fetched here so the popup can read it. It does not count as a connection.
	const hasConnection = hasOutboundConnection || incomingLinks.length > 0;
	const isResolving = loading || (!hasOutboundConnection && incomingLinksLoading);
	useBylineConnectionsLoadTiming(
		contentId,
		isResolving,
		hasConnection,
		outgoing.error || (!hasOutboundConnection ? incoming.error : null),
	);

	const outboundState = useOutboundBylineSsrState(contentId);
	const reservedIconCount = getReservedBylineIconCount(outboundState, devDocsConnection !== null);
	const shouldRenderByline = hasConnection || reservedIconCount > 0;

	// Fire a single "viewed" event the first time the byline actually renders its
	// content — skip while resolving and while there is nothing to show (both cases
	// early-return before the trigger is visible).
	useEffect(() => {
		if (hasFiredViewedRef.current || isResolving || !shouldRenderByline) {
			return;
		}
		hasFiredViewedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'byline',
				actionSubjectId: BYLINE_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
			},
		}).fire();
	}, [createAnalyticsEvent, isResolving, shouldRenderByline]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		// Navigating away lands here too — the popup fires `onClose` once the panel or the modal
		// opens — and that is not a dismissal: it reports its own event, and focus belongs to
		// whatever is opening rather than back on the trigger.
		if (navigatedRef.current) {
			return;
		}
		// What is left is every real dismissal — the header X, Esc and outside clicks — so this is
		// one close event per close, mirroring the modals.
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: MENU_CLOSE_ACTION_SUBJECT_ID,
				source: MENU_SOURCE,
			},
		}).fire();
		// Esc / outside-click close: return focus to the trigger without scrolling.
		triggerRef.current?.focus({ preventScroll: true });
	}, [createAnalyticsEvent]);
	const handleNavigate = useCallback(() => {
		navigatedRef.current = true;
		setIsOpen(false);
	}, []);
	const handleOpenPreviewPanel = useCallback(() => {
		navigatedRef.current = true;
		openTeamworkGraphPreviewPanel();
		setIsOpen(false);
	}, [openTeamworkGraphPreviewPanel]);
	// `navigatedRef` suppresses the focus return: focus belongs to the modal that is opening, not
	// back on the byline trigger.
	const handleOpenShareContext = useCallback(() => {
		navigatedRef.current = true;
		setIsOpen(false);
		setHasOpenedShareContext(true);
		setIsShareContextOpen(true);
	}, []);
	const handleCloseShareContext = useCallback(() => setIsShareContextOpen(false), []);
	// The popup has no chat of its own, so the modal's Rovo button opens the preview panel with the
	// chat already seeded. The modal closes itself; the popup closed when it opened.
	const handleAskRovoFromShareContext = useCallback(
		(prompt: string) => openTeamworkGraphPreviewPanelWithRovo(prompt),
		[openTeamworkGraphPreviewPanelWithRovo],
	);
	const handleToggle = useCallback(() => {
		navigatedRef.current = false;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: BYLINE_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
			},
		}).fire();
		setIsOpen((wasOpen) => !wasOpen);
	}, [createAnalyticsEvent]);

	if (isResolving) {
		// Reuse the SSR/loadable placeholder so the loading footprint is pixel-identical across the
		// SSR → client-load → data-load → item handoff (no skeleton → item shift). It renders the
		// disabled app-icon placeholder only on a positive signal of connections (a 'present' outbound
		// SSR result or a Dev Docs connection), the logo-and-label placeholder for inbound-only pages,
		// and nothing otherwise, so empty pages keep no footprint instead of flashing a collapsing
		// skeleton. An 'unavailable' outbound result is not a signal — see `getReservedBylineIconCount`.
		// Holding it while inbound resolves (only when there is no outbound/Dev Docs signal) keeps the
		// inbound-only hand-over shift-free. CCINT-8752 CCINT-8932
		return (
			<BylineConnectionsPlaceholder
				contentId={contentId}
				connectionAncestors={connectionAncestors}
			/>
		);
	}

	if (!shouldRenderByline) {
		return null;
	}

	return (
		<BylineConnectionsWrapper appearance="loaded">
			<Popup
				isOpen={isOpen}
				onClose={handleClose}
				placement="bottom-start"
				shouldReturnFocus={false}
				content={() => (
					<ConnectionsByLineMenu
						groups={groups}
						links={links}
						incomingLinks={incomingLinks}
						relatedContentLinks={relatedContentLinks}
						atAGlance={atAGlance}
						devDocsConnection={devDocsConnection}
						contentId={contentId}
						showFooter={links.length > 0}
						refreshLinkUrl={refreshLinkUrl}
						onNavigate={handleNavigate}
						onOpenPreviewPanel={handleOpenPreviewPanel}
						onOpenShareContext={handleOpenShareContext}
						onClose={handleClose}
					/>
				)}
				trigger={({ ref, ...triggerProps }: PopupTriggerProps) => (
					<Tooltip content={getTooltipContent(isOpen, formatMessage)}>
						{({
							ref: tooltipRef,
							onClick: onTooltipClick,
							...tooltipProps
						}: TooltipTriggerProps) => (
							<BylineConnectionsTrigger
								appearance="loaded"
								links={links}
								additionalIcons={devDocsIcons}
								minIcons={reservedIconCount}
								buttonProps={{
									...tooltipProps,
									...triggerProps,
									ref: (node: HTMLButtonElement | null) => {
										triggerRef.current = node;
										assignRef(ref, node);
										tooltipRef(node);
									},
									onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
										onTooltipClick(event);
										handleToggle();
									},
									'aria-expanded': isOpen,
									'aria-haspopup': 'dialog',
								}}
							/>
						)}
					</Tooltip>
				)}
			/>
			{/* Kept mounted after the first open so the modal's close transition can finish. */}
			{hasOpenedShareContext && (
				<ShareContextModalLoadable
					isOpen={isShareContextOpen}
					onClose={handleCloseShareContext}
					contentId={contentId}
					onAskRovo={handleAskRovoFromShareContext}
				/>
			)}
		</BylineConnectionsWrapper>
	);
};

// The wrapper is above `BylineConnectionsInner`, not inside it: an `AnalyticsListener` only sees
// events fired by its descendants, and the byline fires its own.
export const BylineConnections: (props: any) => React.JSX.Element = withSmartCardProviderContext(
	(props: BylineConnectionsProps) => (
		<TwgTraceIdAnalyticsWrapper>
			<BylineConnectionsInner {...props} />
		</TwgTraceIdAnalyticsWrapper>
	),
);
